import type { ManagerStatusName } from './workplaceApi';

/**
 * Словарь статусов — ОДИН на всю систему (ТЗ §2.6, правило «одно имя — один счёт»).
 *
 * Статус-бар кабинета, блок в левом баре, пульт руководителя и фильтры журнала
 * обязаны звать один и тот же статус одинаково и красить одним цветом. Второй
 * список названий рядом с первым расходится всегда — и тогда «Перерыв» на
 * одном экране оказывается «На паузе» на другом.
 */
export interface StatusLook {
  label: string;
  /** Цвет полосы и точки — токен боевой палитры админки. */
  color: string;
  /** Фон подложки под полосой: тот же цвет, приглушённый. */
  soft: string;
  text: string;
}

export const STATUS_LOOK: Record<ManagerStatusName, StatusLook> = {
  ready: {
    label: 'Готов',
    color: 'var(--ops-success)',
    soft: 'var(--ops-success-soft)',
    text: 'var(--ops-text)',
  },
  on_call: {
    label: 'В разговоре',
    color: 'var(--ops-primary)',
    soft: 'var(--ops-primary-soft)',
    text: 'var(--ops-text)',
  },
  processing: {
    label: 'Обработка',
    color: 'var(--ops-info)',
    soft: 'var(--ops-info-soft)',
    text: 'var(--ops-text)',
  },
  break: {
    label: 'Перерыв',
    color: 'var(--ops-warning)',
    soft: 'var(--ops-warning-soft)',
    text: 'var(--ops-text)',
  },
  away: {
    label: 'Отошёл',
    color: 'var(--ops-danger)',
    soft: 'var(--ops-danger-soft)',
    text: 'var(--ops-text)',
  },
};

export function statusLook(status: ManagerStatusName | undefined): StatusLook {
  return STATUS_LOOK[status ?? 'ready'] ?? STATUS_LOOK.ready;
}

/**
 * Часы:минуты:секунды — так, как их читает человек в таймере статуса.
 *
 * ⛔⛔ ЗА СУТКАМИ СЧЁТЧИК ПЕРЕСТАЁТ БЫТЬ СЧЁТЧИКОМ (`s408`, Ж1 карты `prov1`).
 * В шапке на КАЖДОМ экране висело «Отошёл · Отлучился · 750:28:41» — 31 день в
 * часах. Человек так не говорит, и главное — это уже не «сколько идёт перерыв»,
 * а «статус забыли переключить месяц назад»: секунды в таком числе не значат
 * ничего, а само оно нечитаемо (op-rules §31 — одна короткая фраза словами).
 *
 * ⛔ ПОРОГ — СУТКИ, А НЕ «БОЛЬШОЕ ЧИСЛО». До суток счётчик отвечает на вопрос
 * «сколько уже», и часы с секундами тут уместны; после суток вопрос другой —
 * «это вообще сегодня?», и ответ на него даётся днями.
 */
export function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (value: number) => String(value).padStart(2, '0');

  /*
   * ⛔ ТЕКСТ ОБЯЗАН БЫТЬ КОРОЧЕ ПРЕЖНЕГО, А НЕ ПРОСТО ПОНЯТНЕЕ. Первый заход
   * писал «больше 31 день» — и сторож регрессий покраснел на ратифицированной
   * компоновке кабинета оператора: место таймера 4,8 % ширины экрана, а строка
   * заняла 13,6 %. Заодно это было неграмотно: после «больше» идёт родительный
   * падеж. Дни без предлога короче и прежних «750:28:41», и любой приписки.
   */
  if (seconds >= 86400) {
    const dney = Math.floor(seconds / 86400);
    const hvost = dney % 100;
    const edinica = hvost > 10 && hvost < 20 ? 0 : dney % 10;
    const slovo = edinica === 1 ? 'день' : edinica >= 2 && edinica <= 4 ? 'дня' : 'дней';

    return `${dney} ${slovo}`;
  }

  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}
